// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {QueryBlockEl} from 'common/query-builder/query-block/panel';
import {SelectorType} from 'common/query-builder/query-block/constants';
import {propertyItemToSegmentation} from 'common/query-builder/query-block/properties-menu/util';
import {QueryEntrySectionEl} from 'common/query-builder/query-entry-section/panel';
import {RowLabelType} from 'common/query-builder/query-entry-section/types';
import {PropertyType} from 'common/report/constants';
import {MenuType} from 'common/query-builder/query-block/types';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {Translatable} from 'common/i18n';

import type {RHSPanel} from '../index';
import {RHSInputType, TemplateField} from '../types';
import {FieldLabel} from './FieldLabel';

export const EventAndListPropertySelector = ({
  cmp,
  eventName,
  propertyName,
  propertyKey,
  label,
  disabled,
  tooltipText,
  event,
  propertyNameLabel,
  propertyKeyLabel,
  showReminderTooltip,
}: {
  cmp: RHSPanel; // TODO(RHS): change variable name
  eventName: string;
  propertyName: string;
  propertyKey: string;
  label: Translatable;
  disabled: boolean;
  tooltipText: Translatable;
  event: string;
  propertyNameLabel: string;
  propertyKeyLabel: string;
  showReminderTooltip?: boolean;
}) => {
  return (
    <div sel='.event-mapping-row.short-field'>
      <FieldLabel fieldName={cmp.print(label)} tooltipText={cmp.print(tooltipText)} />
      {/* Event selector */}
      <div>
        <QueryEntrySectionEl
          sel='.query-entry-section'
          disabled={disabled}
          addEntryLabel={({t}) => t(`boards.rhs.event-list-property-placeholder`)}
          addEntryButtonLabel={({t}) => t(`boards.rhs.event-list-property-placeholder`)}
          projectId={parseInt(cmp.getContext(`currentProject`).id, 10)}
          trackName='templates'
          disableAddEventFilters
          disableCloneEntry
          disableDeleteEntry={false}
          disableFirstTimeEventFilters
          rowLimit={1}
          rowLabelType={RowLabelType.None}
          rowLabelPrefixIcon='event'
          onUpdate={(updates) => {
            if (updates.entries.length) {
              cmp.helpers.updateField(updates, event as TemplateField, RHSInputType.Event);
            } else {
              cmp.helpers.removeField(eventName as TemplateField);
              cmp.helpers.removeField(propertyName as TemplateField);
              cmp.helpers.removeField(propertyKey as TemplateField);
            }
          }}
          onClick={() => cmp.update()}
        />
      </div>

      {cmp.state.fields[eventName] && (
        // List property selector
        <div sel='.property-selector'>
          <div sel='.list-property'>
            <SvgIconEl sel='.property-icon' attrs={{icon: `type-list`}} />
            <QueryBlockEl
              type={MenuType.Properties}
              projectId={parseInt(cmp.getContext(`currentProject`).id, 10)}
              selectorType={SelectorType.Button}
              selectorIcon=''
              theme={ButtonTheme.QueryBuilder}
              selected={cmp.state.fields[propertyName]}
              typeFilter={[PropertyType.List]}
              eventNames={[cmp.state.fields[eventName].value]}
              styleOverride={`.mp-query-block-selector-inner{padding: 3px 3px 3px 0;}`}
              label={propertyNameLabel}
              onSelect={(selection) => {
                cmp.helpers.updateField(selection.item, propertyName as TemplateField, RHSInputType.List);
                cmp.helpers.removeField(propertyKey as TemplateField);
              }}
              onClick={() => cmp.update()}
            />
          </div>
          {cmp.state.fields[propertyName] && (
            // List property key selector
            <div>
              <QueryBlockEl
                type={MenuType.Properties}
                projectId={parseInt(cmp.getContext(`currentProject`).id, 10)}
                selectorType={SelectorType.Button}
                theme={ButtonTheme.QueryBuilder}
                selectorIcon={
                  cmp.state.fields[propertyKey]
                    ? cmp.state.fields[propertyKey].type === `string`
                      ? `type-text`
                      : `type-${cmp.state.fields[propertyKey].type}`
                    : ``
                }
                selected={cmp.state.fields[propertyKey]}
                styleOverride={`.mp-query-block-selector-inner{padding: 3px 3px 3px 20px; width: fit-content;}`}
                label={propertyKeyLabel}
                listProperty={propertyItemToSegmentation(
                  cmp.state.fields[propertyName],
                  cmp.state.fields[propertyName]?.resourceType,
                )}
                onSelect={(selection) =>
                  cmp.helpers.updateField(selection.item, propertyKey as TemplateField, RHSInputType.Key)
                }
                onClick={() => cmp.update()}
              />
            </div>
          )}
        </div>
      )}
      {showReminderTooltip && (
        <TooltipEl
          attrs={{
            placement: Placement.Left,
            offset: 28,
            skidding: 40,
            'always-show': true,
            'delay-in': 10,
            'delay-out': 10,
            'max-width': `200px`,
          }}
        >
          {cmp.t(`boards.rhs.reminder-tooltip`)}
        </TooltipEl>
      )}
    </div>
  );
};
